import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable, Subject, of, delay } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { ToastrService } from 'ngx-toastr';
import { AccessTokenService } from './access-token.service';
import { AttendanceService } from './attendance.service';
import { ErrorMessageService } from './error-message.service';
import { LocalStorageService } from './local-storage.service';
import { SatisfactionSurveyService } from './satisfaction-survey.service';
import { SessionStorageService } from './session-storage.service';
import { UserService } from './user.service';
import { SatisfactionSurveyAnswerInterface } from '../interfaces/satisfaction-survey-answer.interface';
import { SatisfactionSurveyQuestionInterface } from '../interfaces/satisfaction-survey-question.interface';
import { APP_ROUTES } from '../constants/app-routes.constant';
import { AUTH_CODE_FLOW_CONFIG } from '../constants/auth-config.constant';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isLoadingApiAuthSubject = new BehaviorSubject<boolean>(true);
  private hasApiAuthErrorSubject = new BehaviorSubject<boolean>(false);
  private isLoadingUserAuthSubject = new BehaviorSubject<boolean>(true);
  private hasUserAuthErrorSubject = new BehaviorSubject<boolean>(false);
  private sessionExpiredSubject = new Subject<boolean>();
  private sessionIncompleteSubject = new Subject<string>();
  private sessionForbiddenSubject = new Subject<boolean>();
  public isLoadingApiAuthObservable: Observable<boolean>;
  public hasApiAuthErrorObservable: Observable<boolean>;
  public isLoadingUserAuthObservable: Observable<boolean>;
  public hasUserAuthErrorObservable: Observable<boolean>;
  public sessionExpiredObservable: Observable<boolean>;
  public sessionIncompleteObservable: Observable<string>;
  public sessionForbiddenObservable: Observable<boolean>;
  public satisfactionSurveyQuestions: SatisfactionSurveyQuestionInterface[];
  public skipSatisfactionSurvey: boolean;

  

  constructor(
    private accessTokenService: AccessTokenService,
    private attendanceService: AttendanceService,
    private errorMessageService: ErrorMessageService,
    private localStorageService: LocalStorageService,
    private oAuthService: OAuthService,
    private router: Router,
    private satisfactionSurveyService: SatisfactionSurveyService,
    private sessionStorageService: SessionStorageService,
    private toastrService: ToastrService,
    private userService: UserService
  ) {
    this.isLoadingApiAuthObservable = this.isLoadingApiAuthSubject.asObservable();
    this.hasApiAuthErrorObservable = this.hasApiAuthErrorSubject.asObservable();

    this.isLoadingUserAuthObservable = this.isLoadingUserAuthSubject.asObservable();
    this.hasUserAuthErrorObservable = this.hasUserAuthErrorSubject.asObservable();

    this.sessionExpiredObservable = this.sessionExpiredSubject.asObservable();

    this.sessionIncompleteObservable = this.sessionIncompleteSubject.asObservable();

    this.sessionForbiddenObservable = this.sessionForbiddenSubject.asObservable();


  }

  private async initApiAuth() {

    try {
      await lastValueFrom(this.accessTokenService.getAccessToken());

    } catch (error) {
      this.hasApiAuthErrorSubject.next(true);

      console.error(error);
    }

    this.isLoadingApiAuthSubject.next(false);
  }

  private async initUserAuth() {

    let estaAutenticado: boolean;

    try {
      this.oAuthService.configure(AUTH_CODE_FLOW_CONFIG);

      this.oAuthService.setupAutomaticSilentRefresh({}, 'access_token');

      //console.log(this.oAuthService.hasValidAccessToken());
      estaAutenticado = this.oAuthService.hasValidAccessToken();

      await this.oAuthService.loadDiscoveryDocument();

      this.oAuthService.tokenEndpoint = environment.loginCidadao.tokenEndpoint;

      await this.oAuthService.tryLoginCodeFlow();

      if (this.oAuthService.hasValidAccessToken()) {
        const userInfo = await this.oAuthService.loadUserProfile();

        this.userService.setUserInfo(userInfo['info']);

        const userAccessToken = this.getUserAccessToken();

        const registerUserResponse = await this.registrarUsuario(userAccessToken);

        const userId = registerUserResponse.id.toString();

        this.userService.setUserId(userId);

        await this.attendanceService.startAttendance(userAccessToken, userId);

        this.satisfactionSurveyQuestions = await this.satisfactionSurveyService.getSatisfactionSurveyQuestions(
          userAccessToken
        );

        if (!this.satisfactionSurveyQuestions || !this.satisfactionSurveyQuestions.length) {
          this.skipSatisfactionSurvey = true;
        }

        const loginRedirectUrl = this.localStorageService.getJson('app_loginRedirectUrl');

        if (loginRedirectUrl) {
          this.router.navigate([loginRedirectUrl]);
        }
      } else {
        const accessTokenExpiration = this.oAuthService.getAccessTokenExpiration();

        if (accessTokenExpiration) {
          this.sessionExpiredSubject.next(true);
        }
      }

      this.checkAccessTokenTimer();

      this.isLoadingUserAuthSubject.next(false);

    } catch (error) {
      if(estaAutenticado){
        if (error !== false){
          console.error('Erro na autenticação');
          //error?.error?.forEach(erro => console.log(erro?.message));

          switch(error?.status){
            case 401:{
              this.sessionExpiredSubject.next(true);
              break;
            }
            case 403:{
              this.sessionForbiddenSubject.next(true);
              break;
            }
            default:{
              //this.hasUserAuthErrorSubject.next(true);
              this.hasApiAuthErrorSubject.next(true);
              this.isLoadingUserAuthSubject.next(false);
              //throw error;
            }
          }
          console.error(error);
        }
      }
      else{
        this.isLoadingUserAuthSubject.next(false);
      }
    }

    this.localStorageService.remove('app_loginRedirectUrl');

  }

  private oAuthLogout() {
    this.oAuthService.logOut();

    this.localStorageService.clear();
    this.sessionStorageService.clear();

    setTimeout(() => {
      this.userService.setUserInfo(null);
    }, 2500);
  }

  //TODO: verificar regras de encerramento de sessão futuramento
  private async checkAccessTokenTimer() {
    if (this.isLoggedIn() && (await this.isValidProtocol())) {
      const toleranceTime = 10000; // 10s
      const timeout = this.timeRemainingTokenExpiration() + toleranceTime;
      of(null)
        .pipe(delay(timeout))
        .subscribe(() => {
          this.checkAccessTokenTimer();
        });
    }
  }

  timeRemainingTokenExpiration() {
    const expiration = this.oAuthService.getAccessTokenExpiration();
    const now = new Date().getTime();
    return expiration - now;
  }


  public initAuth() {

    this.oAuthService.events.subscribe(e => console.log(e));

    this.initApiAuth();
    this.initUserAuth();
  }

  public initLoginCidadao(loginRedirectUrl = APP_ROUTES.myAccount.url) {
    this.localStorageService.setJson('app_loginRedirectUrl', loginRedirectUrl);

    this.oAuthService.initCodeFlow();
  }

  public getUserAccessToken() {
    return this.oAuthService.getAccessToken();
  }

  public refreshToken() {
    return this.oAuthService.refreshToken();
  }

  public isLoggedIn(): boolean {
    const userAccessToken = this.getUserAccessToken();

    const hasValidAccessToken = this.oAuthService.hasValidAccessToken();

    if (userAccessToken && !hasValidAccessToken) {
      this.sessionExpiredSubject.next(true);
    }

    return hasValidAccessToken;
  }

  public async isValidProtocol(): Promise<boolean> {
    const userAccessToken = this.getUserAccessToken();

    const currentAttendance = await this.attendanceService.getInfoCurrentAttendance(userAccessToken);

    if (currentAttendance.dtEncerramento) {
      this.sessionExpiredSubject.next(true);
    }

    return Boolean(currentAttendance.dtEncerramento);
  }

  public async logout(satisfactionSurveyAnswers?: SatisfactionSurveyAnswerInterface[], sessionExpired?: boolean) {
    let hasSubmitError = false;

    if (!sessionExpired) {
      try {
        const userAccessToken = this.getUserAccessToken();

        await this.attendanceService.endAttendance(userAccessToken);
      } catch (error) {
        console.error(error);
      }

      if (satisfactionSurveyAnswers) {
        try {
          await this.satisfactionSurveyService.sendSatisfactionSurveyAnswers(satisfactionSurveyAnswers);
        } catch (error) {
          hasSubmitError = true;

          this.errorMessageService.displayErrorToast(error);

          console.error(error);
        }

        if (!hasSubmitError) {
          this.toastrService.success('Agradecemos sua participação na pesquisa.', 'Resposta enviada!');

          setTimeout(() => {
            this.oAuthLogout();
          }, 3000);
        }
      } else {
        this.oAuthLogout();
      }
    } else {
      this.oAuthLogout();
    }
  }

  private async registrarUsuario(userAccessToken: string){
    try{
      const registerUserResponse = await this.userService.registerUser(userAccessToken);
      return registerUserResponse;
    }
    catch(error){
      if(error?.status == 0){
        console.error('Erro desconhecido');
        console.error(error);
        this.hasApiAuthErrorSubject.next(true);
        this.isLoadingUserAuthSubject.next(false);
        throw false;
      }
      if(error?.status == 400){
        console.error('Erro no serviço de registro');
        error.error.forEach(erro => console.error(erro?.message)); 
        this.sessionIncompleteSubject.next(error.error[0].message);
        throw false;
      }
      throw error;
    }
  }
}
